import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container, Button, Input, Label, InputGroupText, InputGroup, Modal } from 'reactstrap'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { toast } from 'react-toastify'
import NumberFormat from 'react-number-format'
import Select from 'react-select'
import SelectCreatable from 'react-select/creatable'
import { useDebouncedCallback } from 'use-debounce'

import { UsersService } from 'services/users'
import { withRouter } from 'react-router'
import { setUser } from 'services/auth'
import { IBGEService } from '../../services/ibge'
import { SchoolsService } from '../../services/schools'
import { campaign } from 'services/sharpspring'
import $RDStation from 'services/rdstation'

const InputBorderRadius = (props) => (
  <Input
    style={{
      borderRadius: 20,
      height: 45,
      paddingLeft: 20,
      letterSpacing: 3,
    }}
    {...props}
  />
)

const commonStyles = {
  container: (provide) => ({ ...provide, flex: 1, height: '45px', color: '#000' }),
  control: (provide) => ({
    ...provide,
    height: '45px',
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: '0 10px',
    fontSize: 14,
    letterSpacing: '3px',
    fontWeight: 'normal',
    color: '#000',
  }),
  placeholder: (styles) => ({ ...styles, backgroundColor: 'transparent' }),
  option: (styles) => ({ ...styles, letterSpacing: 0 }),
  input: (provide) => ({
    ...provide,
    backgroundColor: '#fff',
    borderRadius: 20,
    color: '#000',
    letterSpacing: 0,
  }),
}

const SectionRegister = ({ utmSource }) => {
  const [scrollingLongContent, setScrollingLongContent] = React.useState(false)
  const [UFList, setUFList] = useState([])
  const [citiesList, setCitiesList] = useState([])
  const [schoolsList, setSchoolsList] = useState([])
  const [school, setSchool] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [formStep, setFormStep] = useState(0)

  const schema = useMemo(() => {
    if (formStep === 0) {
      return yup.object().shape({
        Nome: yup
          .string()
          .required('Por favor, preencha seu nome completo')
          .max(100, 'Limite máximo é de 100 caracteres')
          .nullable(),
        Email: yup
          .string()
          .required('Por favor, preencha seu e-mail')
          .max(50, 'Limite máximo é de 50 caracteres')
          .nullable(),
        Senha: yup
          .string()
          .required('Por favor, preencha uma senha')
          .min(6, 'Mínimo permitido é de 6 caracteres')
          .max(15, 'Máximo permitido é de 15 caracteres')
          .nullable(),
      })
    }

    return yup.object().shape({
      Celular: yup
        .string()
        .required('Por favor, preencha seu celular')
        .max(15, 'Limite máximo é de 15 caracteres')
        .nullable(),
      Estado: yup
        .object()
        .shape({ value: yup.string().required('Por favor, selecione uma UF').nullable() })
        .required(),
      Cidade: yup
        .object()
        .shape({ value: yup.string().required('Por favor, selecione uma cidade').nullable() })
        .required(),
      NomeEscola: yup
        .object()
        .shape({ value: yup.string().min(3).required('Por favor, selecione uma escola').nullable() }),
      TermosDeUso: yup.boolean().oneOf([true], 'Você deve aceitar o termo'),
    })
  }, [formStep])

  const setSchoolDebounced = useDebouncedCallback((value) => {
    setSchool(value)
  }, 300)
  const { errors, control, watch, setValue, register, handleSubmit } = useForm({ resolver: yupResolver(schema) })
  const { executeRecaptcha } = useGoogleReCaptcha()

  const UFSelected = watch('Estado')
  const citiesSelected = watch('Cidade')
  const schoolSelected = watch('NomeEscola')

  const registerCampaign = useCallback(async (data, value) => {
    try {
      await campaign({
        emailAddress: data.Email,
        firstName: data.Nome,
        escola: value.NomeEscola.label,
        mobilePhoneNumber: data.Celular,
        state: data.Estado,
        city: data.Cidade,
        origem: utmSource,
      })
    } catch (ex) {
      console.error(ex)
    }

    try {
      await $RDStation.conversation('cadastro_mural_sisu', {
        company_name: value.NomeEscola.label,
        name: data.Nome,
        email: data.Email,
        city: data.Cidade,
        state: data.Estado,
        mobile_phone: data.Celular,
      })
    } catch (ex) {
      console.error(ex)
    }
  }, [utmSource])

  const onSubmit = useCallback(
    async (value) => {
      if (formStep === 0) {
        setFormStep(1)
        return
      }

      const Celular = value.Celular.replace(/\(/g, '').replace(/\)/g, '').replace(/-/g, '').replace(/ /g, '')

      const Captcha = await executeRecaptcha('cadastro_pagina')

      const data = {
        ...value,
        Celular,
        Captcha,
        TermosDeUso: value.TermosDeUso,
        PoliticaPrivacidade: value.TermosDeUso,
        LGPD: true,
        Estado: value.Estado.value,
        Cidade: value.Cidade.value,
      }

      if (value.NomeEscola.label === value.NomeEscola.value) {
        data.NomeEscola = value.NomeEscola.label
        data.IdEscolaINEP = null
      } else {
        data.NomeEscola = null
        data.IdEscolaINEP = Number(value.NomeEscola.value)
      }

      try {
        const createUserResponse = await UsersService.create(data)

        if (createUserResponse.status === 200) {
          await registerCampaign(data, value)

          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Lead')
          }

          setUser({
            ...data,
            Id: createUserResponse.data
          })

          window.location.href = '/cadastro-aprovados-sisu'
          return
        }
      } catch (e) {
        if (e.response?.data) toast.error(e.response.data)
      }
    },
    [executeRecaptcha, formStep, registerCampaign]
  )

  const getAllUF = useCallback(async () => {
    const list = await IBGEService.loadUF()
    setUFList(list.map((uf) => ({ label: `${uf.sigla} - ${uf.nome}`, value: uf.sigla })))
  }, [])

  const getCitiesByUF = useCallback(async (uf) => {
    try {
      const response = await SchoolsService.getCitiesByUF(uf)
      if (response.status === 200) {
        setCitiesList(response.data.map((city) => ({ label: city, value: city })))
      }
    } catch (e) {
      console.log(e.message)
    }
  }, [])

  const getSchoolsByCityAndUF = useCallback(async (params) => {
    try {
      const response = await SchoolsService.getSchools(params)
      if (response.status === 200) {
        setSchoolsList(response.data.map((school) => ({ label: school.Value, value: school.Key })))
      }
    } catch (e) {
      console.log(e.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleSchool = useCallback(
    (target) => {
      setIsLoading(true)
      setSchoolDebounced(target)
    },
    [setSchoolDebounced]
  )

  useEffect(() => {
    getAllUF()
  }, [getAllUF])

  useEffect(() => {
    if (UFSelected) {
      getCitiesByUF(UFSelected.value)
    }
  }, [UFSelected, getCitiesByUF])

  useEffect(() => {
    if (UFSelected && citiesSelected && school) {
      getSchoolsByCityAndUF({ busca: school, uf: UFSelected.value, cidade: citiesSelected.value })
    }
  }, [UFSelected, citiesSelected, getCitiesByUF, getSchoolsByCityAndUF, school, schoolSelected])

  const formRef = useRef(null)

  return (
    <div id="cadastro" className="section section-nucleo-icons" style={{ backgroundColor: '#E94B4A', paddingTop: 100 }}>
      <Container className="text-center">
        <h2
          className="title"
          style={{
            color: '#FFF',
            fontWeight: 600,
            letterSpacing: 3,
            marginTop: 0,
          }}
        >
          Cadastre-se
        </h2>
        <br />
        <p className="description" style={{ color: '#FFF', letterSpacing: 2, marginTop: -20 }}>
          Cadastre-se gratuitamente para acessar os dados de sua escola.
        </p>
        <form ref={formRef} id="cadastro-form" name="cadastro-form" className="register-form" onSubmit={handleSubmit(onSubmit)}>
          <div style={{ display: formStep === 0 ? 'block' : 'none' }}>
            <div className="title-brand" style={{ marginTop: 40, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-single-02" />
                </InputGroupText>

                <Input
                  innerRef={register}
                  name="Nome"
                  id="Nome"
                  placeholder="Nome completo"
                  type="text"
                  style={{
                    borderRadius: 20,
                    height: 45,
                    paddingLeft: 20,
                    letterSpacing: 3,
                  }}
                />
              </InputGroup>
              {errors?.Nome?.message && <p>{errors.Nome.message}</p>}
            </div>

            <div className="title-brand" style={{ marginTop: 15, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-email-85" />
                </InputGroupText>

                <Input
                  innerRef={register}
                  name="Email"
                  placeholder="E-mail"
                  type="email"
                  style={{
                    borderRadius: 20,
                    height: 45,
                    paddingLeft: 20,
                    letterSpacing: 3,
                  }}
                />
              </InputGroup>
              {errors?.Email?.message && <p>{errors.Email.message}</p>}
            </div>

            <div className="title-brand" style={{ marginTop: 15, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-lock-circle-open" />
                </InputGroupText>

                <Input
                  innerRef={register}
                  autoComplete="off"
                  name="Senha"
                  placeholder="Senha"
                  type="password"
                  style={{
                    borderRadius: 20,
                    height: 45,
                    paddingLeft: 20,
                    letterSpacing: 3,
                  }}
                />
              </InputGroup>
              {errors?.Senha?.message && <p>{errors.Senha.message}</p>}
            </div>
          </div>

          <div style={{ display: formStep === 1 ? 'block' : 'none' }}>
            <div className="title-brand" style={{ marginTop: 15, fontSize: 14, letterSpacing: 4, fontWeight: 600 }}>
              Você está quase lá! Complete seus dados para começar a usar.
              <br />
              <br />
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-mobile" />
                </InputGroupText>

                <Controller
                  control={control}
                  name="Celular"
                  render={(innerProps) => (
                    <NumberFormat
                      mask="_"
                      format="(##)#####-####"
                      name="Celular"
                      placeholder="Celular"
                      {...innerProps}
                      customInput={InputBorderRadius}
                    />
                  )}
                />
              </InputGroup>
              {errors?.Celular?.message && <p>{errors.Celular.message}</p>}
            </div>

            <div className="title-brand" style={{ marginTop: 15, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-bank" />
                </InputGroupText>

                <Controller
                  control={control}
                  name="Estado"
                  options={UFList}
                  render={(props) => (
                    <Select
                      {...props}
                      placeholder="Estado"
                      options={UFList}
                      styles={commonStyles}
                      noOptionsMessage={() => 'Nenhuma UF encontrada'}
                    />
                  )}
                />
              </InputGroup>
              {errors?.Estado?.value?.message && <p>{errors.Estado.value.message}</p>}
            </div>

            <div className="title-brand" style={{ marginTop: 15, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-map-big" />
                </InputGroupText>

                <Controller
                  control={control}
                  name="Cidade"
                  options={UFList}
                  render={(props) => (
                    <Select
                      {...props}
                      placeholder="Cidade"
                      options={citiesList}
                      styles={commonStyles}
                      noOptionsMessage={() => 'Nenhuma cidade encontrada'}
                    />
                  )}
                />
              </InputGroup>
              {errors?.Cidade?.value?.message && <p>{errors.Cidade.value.message}</p>}
            </div>

            <div className="title-brand" style={{ marginTop: 15, fontSize: 16 }}>
              <InputGroup>
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-bank" />
                </InputGroupText>

                <Controller
                  control={control}
                  name="NomeEscola"
                  required={true}
                  options={schoolsList}
                  render={(props) => (
                    <SelectCreatable
                      {...props}
                      formatCreateLabel={(text) => `Criar a opção "${text}"`}
                      isLoading={isLoading}
                      allowCreateWhileLoading={false}
                      noOptionsMessage={(text) =>
                        text.inputValue
                          ? `Nenhuma escola "${text.inputValue}" foi encontrada`
                          : 'Digite algo para iniciar a pesquisa'
                      }
                      loadingMessage={(text) => `Pesquisando por "${text.inputValue}", aguarde...`}
                      onChange={(newValue) => setValue('NomeEscola', newValue)}
                      onInputChange={handleSchool}
                      placeholder="Escola"
                      options={schoolsList}
                      styles={commonStyles}
                    />
                  )}
                />
              </InputGroup>
              {errors?.NomeEscola?.value?.message && <p>{errors.NomeEscola.value.message}</p>}
            </div>

            <div className="title-brand" align="left" style={{ textAlign: 'start' }}>
              <div style={{ marginTop: 20, paddingLeft: 20 }}>
                <Label
                  style={{
                    letterSpacing: 3,
                    lineHeight: 1.6,
                    cursor: 'pointer',
                  }}
                  check
                >
                  <Input
                    innerRef={register}
                    name="TermosDeUso"
                    style={{ border: 0, borderRadius: 20, cursor: 'pointer' }}
                    type="checkbox"
                  />{' '}
                  Li e concordo com os
                  <span className="form-check-sign">
                    <span className="check"></span>
                  </span>
                </Label>
                &nbsp;
                <a
                  href="#!"
                  onClick={() => setScrollingLongContent(true)}
                  style={{ color: '#fff', fontWeight: 500, letterSpacing: 3, cursor: 'pointer' }}
                >
                  Termos de Uso e
                </a>&nbsp;<a style={{ color: '#fff', fontWeight: 500, letterSpacing: 3, cursor: 'pointer' }} href="/politica-de-privacidade">Política de Privacidade.</a>
              </div>
            </div>

            <Modal isOpen={scrollingLongContent} toggle={() => setScrollingLongContent(false)}>
              <div className="modal-header">
                <button
                  aria-label="Close"
                  className="close"
                  data-dismiss="modal"
                  type="button"
                  onClick={() => setScrollingLongContent(false)}
                >
                  <span aria-hidden={true}>×</span>
                </button>
                <h5 style={{ fontWeight: 600 }} className="modal-title" id="exampleModalLongTitle">
                  Mural de Aprovados SiSU
                  <br />
                  <span style={{ fontWeight: 400 }}>Termos de Uso e Política de Privacidade</span>
                </h5>
              </div>
              <div className="modal-body">
                <div style={{ fontWeight: 600, marginBottom: 20 }}>Termos de Uso</div>
                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>1. Descrição da ferramenta</span>
                  <br /> <br />A ferramenta “MURAL de Aprovados SiSU” consiste em uma aplicação gratuita que indica, a
                  partir de uma lista de nomes de alunos, quais deles foram aprovados pela última chamada regular do
                  SiSU divulgada. A ferramenta fornece essas informações de maneira totalmente gratuita, de forma que o
                  uso da aplicação não implica em pagamento ou fechamento de contrato com as empresas que a
                  desenvolveram.
                </p>
                <br />
                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>2. Cadastro</span>
                  <br /> <br />
                  Para utilizar a ferramenta, é necessário preencher o formulário disponibilizado na página:
                  http://muralsisu.evolucional.com.br/cadastro.
                </p>
                <br />
                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>3. Lista de aprovados</span>
                  <br /> <br />
                  <span style={{ fontWeight: 600 }}>3.1.</span> Para ter acesso à lista de aprovados, é necessário fazer
                  o upload de uma planilha Excel no formato .xls com os nomes dos alunos que se deseja consultar e que
                  fizeram o último ENEM, conforme indicado na página da ferramenta. Uma vez que a planilha é enviada, o
                  sistema vai fornecer uma lista com as seguintes informações: <br />
                  <br />
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.1.</span> Nome do aluno
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.2.</span> Status (aprovado, não encontrado ou lista de
                      espera)
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.3.</span> Sigla da IES (Instituição de Ensino)
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.4.</span> Nome do curso
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.5.</span> Classificação
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.6.</span> Modalidade
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.1.7.</span> Número de inscrição fornecido pelo sistema
                    </li>
                  </ul>
                  <br />
                  <span style={{ fontWeight: 600 }}>3.2.</span> A consulta dos aprovados se baseia exclusivamente no
                  nome dos alunos. Cada nome da lista é procurado na lista de aprovados de todos os cursos na chamada
                  regular do SiSU. Isso significa que, apesar de pouco provável, pode haver casos de alunos que não
                  foram aprovados, mas que estarão relacionados com algum curso, por terem um homônimo exato. <br />{' '}
                  <br />
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.2.1.</span> Caso haja dois ou mais alunos com nomes iguais
                      entre os aprovados no SiSU, esses alunos estarão sinalizados na lista fornecida para facilitar sua
                      identificação. Você poderá verificar qual aluno é de fato da sua escola a partir do número de
                      inscrição.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.2.2.</span> Se o nome do aluno indicado no arquivo enviado não
                      for o nome completo do estudante, o resultado poderá ser diferente da realidade.
                    </li>
                  </ul>
                  <br />
                  <span style={{ fontWeight: 600 }}>3.3.</span> Os alunos que forem aprovados terão as informações de
                  todas as colunas preenchidas. <br /> <br />
                  <span style={{ fontWeight: 600 }}>3.4.</span> Quando apenas as colunas “Nome do aluno” e “Status”
                  estiverem preenchidas, sendo esta última com a informação “Não encontrado”, tem-se duas
                  possibilidades: <br /> <br />
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.4.1.</span> O aluno em questão não foi aprovado em nenhum
                      curso.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>3.4.2.</span> O nome do aluno apresentado na planilha diverge do
                      nome que o estudante usou no ENEM e, por causa disso, ele não foi encontrado no sistema do SiSU.
                    </li>
                  </ul>
                  <br />
                  <span style={{ fontWeight: 600 }}>3.5.</span> A lista de aprovados fornecida pela ferramenta refere-se
                  à última chamada regular do SiSU publicada. <br /> <br />
                </p>

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>4. Sobre os dados informados.</span>
                  <br /> <br />
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>4.1.</span> A desenvolvedora da ferramenta se compromete a
                      preservar os resultados tanto da escola quanto dos alunos.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>4.2.</span> A desenvolvedora da ferramenta não se responsabiliza
                      caso alguma escola busque o resultado de alunos matriculados em outras instituições.
                    </li>
                  </ul>
                  <hr />
                </p>

                <div style={{ fontWeight: 600, marginBottom: 20 }}>Política de Privacidade</div>
                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>1. Nosso Compromisso com sua Privacidade</span>
                  <br /> <br />A EVOLUCIONAL EDITORA E SERVIÇOS DE INFORMAÇÃO E INTERNET, CNPJ 23.447896/0001-57,
                  endereço Rua Embiruçu 185 – Loteamento Alphaville - Campinas – SP – 13.098-320, telefone +55 19
                  3756-1640 está comprometida em proteger a privacidade de seus clientes. A política de privacidade da
                  EVOLUCIONAL (a “Política de Privacidade”) se aplica a todas as informações pessoais coletadas por nós
                  ou submetidas a nós, seja off-line ou on-line, incluindo informações pessoais coletadas ou submetidas
                  através de nossos websites (nossos “Websites”) e quaisquer sites de dispositivos móveis, aplicativos e
                  widgets, e outros recursos interativos de dispositivos móveis, (coletivamente, nossos “Apps”), através
                  das nossas páginas oficiais em mídias sociais que controlamos (nossas “Páginas de Mídia Social”) bem
                  como através de mensagens de e-mail em formato HTML que enviamos a você (coletivamente, incluindo as
                  Páginas de Mídia Social, Apps, e Websites, os “Sites”). Ao clicar na caixa apropriada, usar os
                  Websites ou nos fornecer Informações Pessoais, você reconhece e confirma que você leu, compreendeu e
                  que você concorda com os termos e condições desta Política de Privacidade. Portanto, você concorda com
                  a coleta, uso, processamento, transferência e divulgação de suas informações pessoais, conforme
                  descrito nesta Política de Privacidade.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>2. Informações Pessoais</span>
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>Quais informações pessoais nós coletamos?</span>
                  <br />
                  “Informações Pessoais” são informações que identificam você como um indivíduo, tais como:
                  <br />
                  <br />
                  ● Nome
                  <br />
                  ● Nome de usuário
                  <br />
                  ● Endereço postal (incluindo endereços de cobrança e de entrega)
                  <br />
                  ● Número de telefone (incluindo números de telefone de casa residencial e de celular móvel)
                  <br />
                  ● Endereço de e-mail
                  <br />
                  ● País de residência
                  <br />
                  ● Endereço de IP
                  <br />
                  ● Registros de acesso (incluindo data e horário de acesso associado com seu Endereço de IP)
                  <br />
                  ● Nome e endereço da escola
                  <br />
                  ● Nome e endereço da empresa
                  <br />
                  ● Informações do navegador e do dispositivo
                  <br />
                  ● Informações dos arquivos de log de servidor
                  <br />
                  ● Informações coletadas através de cookies, pixel tags e outras tecnologias
                  <br />
                  ● Dados de utilização de App
                  <br />
                  ● Informações demográficas e outras informações fornecidas por você
                  <br />
                  ● Informações de localização
                  <br />
                  ● Informações agregadas
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>Como nós coletamos informações pessoais?</span>
                  <br />
                  Nós podemos coletar Informações Pessoais de diversas maneiras, incluindo:
                  <br />
                  <br />
                  ● Através dos Sites: Podemos coletar Informações Pessoais por meio dos Sites, por exemplo, quando você
                  se cadastra para receber um serviço, fazer download de um conteúdo ou demonstrar interesse em saber
                  mais sobre produtos e serviços por meio de um contato.
                  <br />
                  <br />
                  ● Off-line: Podemos coletar suas Informações Pessoais off-line, por exemplo, quando você entra em
                  contato com o serviço de atendimento ao cliente.
                  <br />
                  <br />
                  ● De Outras Fontes: Podemos receber suas Informações Pessoais de outras fontes, tais como bancos de
                  dados públicos; parceiros de marketing conjuntos; plataformas de mídia social; de pessoas com as quais
                  você tenha amizade ou às quais você esteja de outra forma conectado em plataformas de mídia social,
                  bem como de outros terceiros. Por exemplo, se você escolher conectar sua conta de mídia social à sua
                  conta do Website, determinadas Informações Pessoais de sua conta de mídia social serão compartilhadas
                  conosco, que podem incluir Informações Pessoais que sejam parte de seu perfil ou dos perfis de seus
                  amigos.
                  <br />
                  <br />
                  ● Através do seu navegador ou dispositivo: Determinadas informações são coletadas pela maioria dos
                  navegadores ou automaticamente através de seu dispositivo. Nós usamos essas informações para fins
                  estatísticos e, também, para garantir que os Sites funcionem adequadamente.
                  <br />
                  <br />
                  ● Através de arquivos de log de servidor: Seu “Endereço de IP” é um número que é automaticamente
                  atribuído ao computador ou dispositivo que você está usando pelo seu Provedor de Serviço de Internet
                  (em inglês, ISP). Um Endereço de IP é identificado e registrado automaticamente em nossos arquivos de
                  log de servidor sempre que um usuário visita os Sites, juntamente com o horário da visita e a(s)
                  página(s) que foram visitadas.
                  <br />
                  <br />
                  ● Usando cookies: Cookies permitem que um servidor de internet transfira dados a um computador ou
                  dispositivo para manutenção de registro e outros propósitos. Caso você não queira que informações
                  sejam coletadas por meio do uso de cookies, há um procedimento simples na maioria dos navegadores que
                  permite que você desative o uso de cookies. Para informações detalhadas sobre os cookies que
                  utilizamos e as finalidades para as quais os utilizamos, por favor, veja o item 3.
                  <br />
                  <br />
                  ● Através do uso de um App: Quando você faz o download e utiliza um App, nós e nossos prestadores de
                  serviços podemos monitorar e coletar dados de utilização do App, tais como a data e horário em que o
                  App acessa os no
                  <br />
                  <br />
                  ● De você: Informações como data de nascimento, gênero e código postal, assim como outras informações,
                  como seus meios de comunicação preferidos, podem ser coletadas quando você as fornecer
                  voluntariamente.
                  <br />
                  <br />
                  ● Localização Física: Podemos coletar a localização física do seu dispositivo.
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>Como utilizamos Informações Pessoais?</span> <br />
                  Podemos utilizar Informações Pessoais:
                  <br />
                  <br />
                  ● Para responder às suas solicitações e atender seus pedidos, como para lhe enviar newsletters,
                  convites, conteúdos, informações sobre nossos produtos e serviços ou para responder às suas perguntas
                  e comentários.
                  <br />
                  <br />
                  ● Para lhe enviar informações administrativas, por exemplo, informações referentes aos Sites e
                  alterações aos nossos termos, condições e políticas. Tendo em vista que essas informações podem ser
                  importantes para a sua utilização dos Sites, você não poderá optar por não receber tais comunicações
                  (opt-out).
                  <br />
                  <br />
                  ● Para lhe fornecer atualizações e anúncios referentes aos nossos produtos, serviços, promoções e
                  programas e para lhe enviar convites para participar de programas e eventos especiais.
                  <br />
                  <br />
                  ● Para permitir que você entre em contato e seja contatado pela equipe comercial.
                  <br />
                  <br />
                  ● Para permitir que você participe em listas de discussão, bate-papo, páginas de perfil e blogs e
                  outros serviços nos quais você possa postar informações e materiais (incluindo nossas Páginas de Mídia
                  Social).
                  <br />
                  <br />
                  ● Para nossas finalidades comerciais, tais como análise e gestão de nossos negócios, pesquisa de
                  mercado, auditorias, desenvolvimento de novos produtos, aprimoramentos dos nossos Sites, melhorias de
                  nossos serviços e produtos, identificação de tendências de uso, avaliação da eficácia de nossas
                  campanhas promocionais, personalização da experiência e conteúdo dos Sites com base em suas atividades
                  anteriores nos Sites, e medição de satisfação de clientes e fornecimento de serviços de atendimento ao
                  cliente (incluindo solução de problemas relacionados a problemas dos clientes).
                  <br />
                  <br />
                  ● Utilizamos informações coletadas por navegadores ou seu dispositivo para fins estatísticos e,
                  também, para garantir que os Sites funcionem adequadamente.
                  <br />
                  <br />
                  ● Para informações detalhadas sobre os cookies que utilizamos e as finalidades para as quais os
                  utilizamos, por favor, veja o item 3.
                  <br />
                  <br />
                  ● Nós podemos utilizar a localização física do seu dispositivo para lhe fornecer conteúdo e serviços
                  personalizados baseados na localização. Podemos, também, compartilhar a localização física do seu
                  dispositivo, juntamente com informações de quais anúncios publicitários você viu e outras informações
                  que coletamos, com nossos parceiros de marketing para permitir que eles lhe forneçam conteúdo mais
                  personalizado e estudem a eficácia de campanhas de publicidade.
                  <br />
                  <br />
                  ● Conforme acreditarmos ser necessário ou apropriado: (a) nos termos da legislação aplicável,
                  incluindo leis de países que não a de seu país de residência; (b) para cumprir com processos legais;
                  (c) para responder a solicitações de autoridades públicas e governamentais, incluindo autoridades
                  públicas e governamentais de países que não a de seu país de residência; (d) para aplicar nossos
                  termos e condições; (e) para proteger nossas operações ou aquelas de qualquer de nossas afiliadas; (f)
                  para proteger nossos direitos, privacidade, segurança ou propriedade e/ou os de nossas afiliadas, seus
                  ou de terceiros; e (g) para nos permitir a perseguir recursos disponíveis ou limitar os danos que
                  possamos suportar.
                  <br />
                  <br />● Seus dados pessoais serão processados pela EVOLUCIONAL para lhe fornecer os produtos e
                  serviços solicitados por você e para atividades de serviço de atendimento ao cliente, atividades de
                  marketing e comerciais. Nenhum parceiro comercial receberá suas Informações Pessoais para seus
                  próprios fins publicitários. A EVOLUCIONAL processa seus dados pessoais em conformidade com a
                  legislação de privacidade e proteção de dados aplicável. Caso você tenha quaisquer perguntas sobre o
                  processamento de seus dados pessoais pela EVOLUCIONAL, ou deseje obter uma cópia das informações sobre
                  você detidas pela EVOLUCIONAL, ou solicitar que dados pessoais incorretos sejam retificados ou
                  apagados, por favor, entre em contato conosco.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>2. Cookies</span>
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>O que são Cookies?</span>
                  Cookies são pequenos arquivos de texto ou fragmentos de informação que são baixados em seu computador,
                  smartphone ou qualquer outro dispositivo com acesso à internet quando você visita nossa aplicação.
                  <br />
                  <br />
                  Eles contêm informações sobre a sua navegação em nossas páginas e retêm apenas informações
                  relacionadas às suas preferências. <br />
                  <br />
                  Assim, essa página consegue armazenar e recuperar os dados sobre os seus hábitos de navegação como,
                  por exemplo, melhorar a sua experiência de uso. É importante frisar que eles não contêm informações
                  pessoais específicas, como dados sensíveis ou bancários.
                  <br />
                  <br />
                  O seu navegador armazena os cookies no disco rígido, mas ocupam um espaço de memória mínimo, que não
                  afetam o desempenho do seu computador. A maioria das informações são apagadas logo ao encerrar a
                  sessão.
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>Por que usamos Cookies?</span>
                  <br />
                  <br />
                  A Evolucional utiliza Cookies para fornecer a melhor experiência de uso, tornando nossas aplicações
                  mais fáceis e personalizadas, tendo por base suas escolhas e comportamento de navegação.
                  <br />
                  <br />
                  Assim, buscamos entender como você utiliza nossas aplicações e ajustar o conteúdo para torná-lo mais
                  relevante para você, além de lembrar de suas preferências.
                  <br />
                  <br />
                  Os Cookies participam deste processo porquanto armazenam, leem e executam os dados necessários para
                  cumprir com o nosso objetivo.
                  <br />
                  <br />
                  Respeitamos o direito à privacidade e você poderá, a qualquer momento, optar pela desativação dos
                  cookies. Essa ação, contudo, pode afetar a disponibilidade de algumas ferramentas e funcionalidades do
                  site ou de nossos serviços, comprometendo seu correto e esperado funcionamento e, eventualmente,
                  comprometendo uma experiência mais personalizada.
                  <br />
                  <br />
                  Importante esclarecer que não nos responsabilizamos pelo uso de cookies por terceiros. Os cookies
                  colocados por terceiros podem, eventualmente, continuar a monitorar as suas atividades online, mesmo
                  depois de ter saído do nosso site, sendo recomendável que você limpe seus dados de navegação
                  regularmente. Os cookies, quanto a sua finalidade, podem ser:
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>● Cookies necessários: </span>são cookies essenciais que
                  possibilitam a navegação em nossas aplicações e o acesso a todos os recursos; sem estes, nossos
                  serviços podem apresentar mal desempenho ou não funcionar.
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>● Cookies de desempenho:</span> são cookies que otimizam a forma que
                  nossas aplicações funcionam, coletando informações anônimas sobre as páginas acessadas.
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>● Cookies de funcionalidade:</span> são cookies que memorizam suas
                  preferências e escolhas (como seu nome de usuário).
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>● Cookies de publicidade:</span>são cookies que direcionam anúncios
                  em função dos seus interesses e limitam a quantidade de vezes que o anúncio aparece.
                  <br />
                  <br />
                  <span style={{ fontWeight: 600 }}>Gerenciamento dos Cookies</span>
                  <br />
                  <br />
                  A instalação dos cookies está sujeita ao seu consentimento. Apesar da maioria dos navegadores estar
                  inicialmente configurada para aceitar cookies de forma automática, você pode rever suas permissões a
                  qualquer tempo, de forma a bloqueá-los, aceitá-los ou ativar notificações para quando alguns cookies
                  forem enviados ao seu dispositivo. <br />
                  <br />
                  Atualmente, na primeira vez que você acessar nossas aplicações, será requerida a sua concordância com
                  a instalação destes. Apenas após a sua aceitação eles serão ativados.
                  <br />
                  <br />
                  Como já dito, você pode, a qualquer tempo e sem nenhum custo, alterar as permissões, bloquear ou
                  recusar os Cookies. Você também pode configurá-los caso a caso. Todavia, a revogação do consentimento
                  de determinados Cookies pode inviabilizar o funcionamento correto de alguns recursos da plataforma.
                  <br />
                  <br />
                  Para gerenciar os cookies do seu navegador, basta fazê-lo diretamente nas configurações do navegador,
                  na área de gestão de Cookies.
                  <br />
                  <br />
                  Você pode acessar tutoriais sobre o tema diretamente nos links abaixo:
                  <br />
                  <br />
                  Se você usa o Internet Explorer.
                  <br />
                  Se você usa o Firefox.
                  <br />
                  Se você usa o Safari.
                  <br />
                  Se você usa o Google Chrome.
                  <br />
                  Se você usa o Microsoft Edge.
                  <br />
                  Se você usa o Opera.
                  <br />
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>4. Segurança</span>
                  <br />
                  <br />
                  Nós empregamos medidas organizacionais, técnicas e administrativas adequadas para proteger as
                  Informações Pessoais sob nosso controle. Infelizmente, nenhuma transmissão de dados pela Internet ou
                  sistema de armazenamento de dados pode ser garantida como sendo 100% segura.
                  <br />
                  <br />
                  Se você tiver motivos para acreditar que a sua interação conosco tenha deixado de ser segura (por
                  exemplo, se você acreditar que a segurança de qualquer conta que você possa ter conosco tenha sido
                  comprometida), por favor, nos notifique do problema imediatamente, entrando em contato conosco.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>5. Acesso, Modificação e Escolhas</span>
                  <br />
                  <br />
                  Nós lhe damos várias opções em relação ao nosso uso e divulgação de suas Informações Pessoais para
                  fins publicitários.
                  <br />
                  <br />
                  ● Caso, a qualquer momento, você deseje parar de receber comunicações de marketing enviadas por nós, a
                  forma mais fácil de fazê-lo seria a utilização da função de cancelamento de inscrição na comunicação
                  de marketing que você recebeu. Você também pode entrar em contato conosco, indicando que você deseja
                  parar de receber nossas comunicações.
                  <br />
                  <br />
                  ● Adicionalmente, nós não divulgaremos suas Informações Pessoais a terceiros, para finalidades de
                  marketing direto.
                  <br />
                  <br />
                  ● Note, por favor, que alterações podem não ter efeito imediato. Faremos o possível para cumprir com a
                  sua solicitação assim que razoavelmente praticável e em nenhuma hipótese mais de 30 dias após receber
                  sua solicitação. Por favor, note que se você solicitar não receber de nós mensagens relacionadas a
                  marketing, nós ainda poderemos lhe enviar mensagens administrativas importantes, e você não pode optar
                  por deixar de receber mensagens administrativas.
                  <br />
                  <br />
                  ● Para revisar, corrigir, atualizar, suprimir, apagar ou de outra forma limitar nossa utilização de
                  suas Informações Pessoais que nos tenham sido anteriormente fornecidas, por favor, entre em contato
                  conosco e descreva a sua solicitação de forma clara. Na sua solicitação, por favor, esclareça quais
                  informações você gostaria que fossem alteradas, se você gostaria que suas Informações Pessoais fossem
                  suprimidas de nosso banco de dados ou nos informe de quais limitações você gostaria que aplicássemos
                  ao uso de suas Informações Pessoais.
                  <br />
                  <br />● Nós tentaremos atender à sua solicitação assim que razoavelmente praticável. Note que poderá
                  haver informações residuais que permanecerão em nossos bancos de dados e outros registros, as quais
                  não serão removidas ou modificadas, conforme solicitado pela legislação aplicável. Além disso, por
                  favor, note que nós poderemos ter de manter determinadas informações para fins de manutenção de
                  registros e/ou para concluir quaisquer transações a que você tenha iniciado antes de solicitar tal
                  mudança ou exclusão (por exemplo, quando você realiza uma compra ou participa de uma promoção, você
                  pode não conseguir modificar ou apagar as Informações Pessoais fornecidas até a conclusão de tal
                  compra ou promoção).
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>6. Período De Retenção</span>
                  <br />
                  <br />
                  Nós manteremos suas Informações Pessoais durante o período necessário para cumprir com os propósitos
                  delineados nesta Política de Privacidade.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>7. Utilização Dos Sites Por Menores De Idade</span>
                  <br />
                  <br />
                  Caso você tenha mais de 16 anos, mas menos de 18 anos, você deve rever os termos dessa Política de
                  Privacidade com um de seus pais ou seu responsável para garantir que ambos compreenderam os termos.
                  Apesar de não sermos responsáveis por determinar a idade de nossos usuários, podemos realizar
                  verificações. Caso descubramos que coletamos informações de um indivíduo com menos de 16 anos,
                  apagaremos essas informações assim que possível e poderemos desativar o acesso desse indivíduo ao
                  website aplicável.
                  <br />
                  <br />
                  Nós oferecemos serviços de consulta que podem ser acessados por menores de idade. Para a proteção da
                  privacidade dos menores, nós exigimos o consentimento dos pais dos usuários que desejarem fazer a
                  consulta do serviço oferecido.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>8. Informações Sensíveis</span>
                  <br />
                  <br />
                  Nós geralmente solicitamos que você não nos envie, e que você não divulgue, quaisquer Informações
                  Pessoais sensíveis (por exemplo, informações relacionadas à origem étnica ou racial, opiniões
                  políticas, religião ou crenças de outra espécie, condição médica ou de saúde, antecedentes criminais
                  ou participação em sindicato) por meio dos Sites, redes sociais ou de qualquer outra forma.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>9. Atualizações desta Política de Privacidade</span>
                  <br />
                  <br />
                  Para a Evolucional, a privacidade e confiança são fundamentais para a nossa relação com você. Estamos
                  sempre nos atualizando para manter os mais altos padrões de segurança. <br />
                  <br />
                  Assim, reservamo-nos o direito de alterar esta Política de Privacidade a qualquer tempo e as mudanças
                  entrarão em vigor logo após a publicação.
                  <br />
                  <br />
                  Ao continuar a navegação nas nossas aplicações após essa mudança se tornar eficaz, você concorda com
                  elas. Aconselhamos que você sempre verifique esta Política de Privacidade.
                  <br />
                  <br />
                  Esta Política de Privacidade foi atualizada em: 26/03/2021.
                </p>
                <br />

                <p style={{ fontWeight: 400 }}>
                  <span style={{ fontWeight: 600 }}>10. Contate-nos</span>
                  <br />
                  <br />
                  Se você tiver quaisquer dúvidas sobre esta Política de Privacidade, por favor, nos contate pelo e-mail
                  contato@evolucional.com.br
                </p>
              </div>
            </Modal>

            {errors?.TermosDeUso?.message && <p style={{ color: '#fff' }}>{errors.TermosDeUso.message}</p>}
          </div>

          <div className="title-brand" style={{ marginTop: 20, fontSize: 16 }}>
            <Button
              style={{
                letterSpacing: 2,
                borderWidth: 1.5,
                borderColor: '#fff',
                fontSize: 16,
                width: '100%',
                textTransform: 'none',
              }}
              className="btn-round"
              color="danger"
              type="submit"
            >
              Enviar
            </Button>
          </div>

          <div className="title-brand" style={{ marginTop: 10 }}>
            {formStep === 1 && (
              <Button
                style={{ letterSpacing: 2, fontSize: 12, width: '100%', textTransform: 'none' }}
                className="btn-round"
                color="danger"
                type="button"
                onClick={() => setFormStep(0)}
              >
                Voltar
              </Button>
            )}
          </div>
        </form>
      </Container>
    </div>
  )
}

export default withRouter(SectionRegister)
